import React, { useEffect, useRef, useState } from "react";
import {
  COIN_SIZE, coinFileName, defaultStyle, drawCoin, isHexColor, normalizeTicker,
  paletteFor, readableTextColor, squareCrop,
  type CoinFill, type CoinShape, type CoinStyle,
} from "../lib/coin-image";
import { apiConfigured, generateCoinImage, imageQuota, type ImageQuota } from "../lib/api";

/**
 * Makes the token's picture, in the browser, from nothing.
 *
 * It produces a FILE and never a URL. Hosting is the irreversible half of token
 * metadata — the image URI is written on chain before authorities are revoked
 * (invariant 1), so where it lives is the operator's decision, made once, and
 * this component must not quietly make it for them by pointing the form at a
 * blob: or data: URL that dies with the tab.
 *
 * Everything is local: no upload, no API, no key, nothing leaves the machine.
 * An uploaded file is read with FileReader and drawn straight to a canvas.
 */
export function CoinImage(props: {
  name: string;
  symbol: string;
  /** The Image URL currently in the form, previewed as a wallet would show it. */
  imageUrl: string;
}) {
  const { name, symbol, imageUrl } = props;
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const [style, setStyle] = useState<CoinStyle>(() => defaultStyle(symbol));
  const [uploaded, setUploaded] = useState<HTMLImageElement | null>(null);
  const [uploadNote, setUploadNote] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiNote, setAiNote] = useState<string | null>(null);
  const [quota, setQuota] = useState<ImageQuota | null>(null);

  /*
      The generator panel appears only when there is an API, a session, and an
      AI binding on that deployment. It is additive in the strictest sense: the
      canvas below draws a coin with no account, no network and no cost, and
      that path is never removed, disabled, or put behind this one. Invariant 13
      is about the wallet and the launch; this is a picture, and the free way to
      make one stays the default.
  */
  useEffect(() => {
    if (!apiConfigured) return;
    let live = true;
    void imageQuota().then((q) => live && setQuota(q));
    return () => { live = false; };
  }, []);

  async function generate() {
    if (!prompt.trim() || busy) return;
    setBusy(true);
    setAiNote(null);
    const r = await generateCoinImage(prompt);
    if (!r.ok) {
      // The reason, verbatim from the API -- "Daily limit of 40 generated
      // images reached. It resets at 00:00 UTC." beats a dead spinner.
      setAiNote(r.detail);
      setBusy(false);
      return;
    }
    const img = new Image();
    img.onload = () => {
      setUploaded(img);
      setUploadNote(
        `Generated ${img.naturalWidth}x${img.naturalHeight}, centre-cropped to ${COIN_SIZE}x${COIN_SIZE} PNG. ` +
        `${r.data.remaining} generations left today.`,
      );
      setQuota((q) => (q ? { ...q, remaining: r.data.remaining, used: q.limit - r.data.remaining } : q));
      setBusy(false);
    };
    img.onerror = () => {
      setAiNote("The model returned an image the browser could not decode.");
      setBusy(false);
    };
    // The model returns JPEG regardless of what is asked for; `mime` says so
    // rather than this code assuming. The canvas re-encodes to PNG on download.
    img.src = `data:${r.data.mime};base64,${r.data.image}`;
  }

  /*
      The ticker follows the form's symbol until the operator edits it here.
      A ref holding the exact value written, not a boolean: an effect reads the
      state captured at the render it was created in, and that stale read is
      what deleted an operator's typed metadata URI once already (invariant 24).
      The same shape of bug, so the same fix.
  */
  const autoTicker = useRef<string>(normalizeTicker(symbol));
  useEffect(() => {
    const next = normalizeTicker(symbol);
    setStyle((s) => {
      if (s.ticker !== autoTicker.current) return s;   // operator's own value
      autoTicker.current = next;
      const p = paletteFor(next);
      return { ...s, ticker: next, colorA: p.colorA, colorB: p.colorB, textColor: readableTextColor(p.colorA) };
    });
  }, [symbol]);

  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    if (uploaded) {
      const { sx, sy, size } = squareCrop(uploaded.naturalWidth, uploaded.naturalHeight);
      ctx.clearRect(0, 0, COIN_SIZE, COIN_SIZE);
      ctx.drawImage(uploaded, sx, sy, size, size, 0, 0, COIN_SIZE, COIN_SIZE);
    } else {
      drawCoin(ctx, style, COIN_SIZE);
    }
    setSaved(null);
  }, [style, uploaded]);

  function edit<K extends keyof CoinStyle>(key: K, value: CoinStyle[K]) {
    setUploaded(null);
    setUploadNote(null);
    setStyle((s) => {
      const next = { ...s, [key]: value };
      // Keep the ticker legible whenever the background moves under it.
      if (key === "colorA") next.textColor = readableTextColor(String(value));
      return next;
    });
  }

  function onFile(file: File | undefined) {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      setUploadNote("That is not an image file.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const square = img.naturalWidth === img.naturalHeight;
        setUploaded(img);
        setUploadNote(
          square
            ? `${img.naturalWidth}×${img.naturalHeight}, resized to ${COIN_SIZE}×${COIN_SIZE}.`
            : `${img.naturalWidth}×${img.naturalHeight} is not square — centre-cropped to ${COIN_SIZE}×${COIN_SIZE}. Wallets render the token square or round, so an uncropped image would be letterboxed by some and stretched by others.`,
        );
      };
      img.onerror = () => setUploadNote("That image could not be decoded.");
      img.src = String(reader.result);
    };
    reader.onerror = () => setUploadNote("That file could not be read.");
    reader.readAsDataURL(file);
  }

  function download() {
    const c = canvas.current;
    if (!c) return;
    const file = coinFileName(name, symbol);
    c.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file;
      a.click();
      URL.revokeObjectURL(url);
      setSaved(file);
    }, "image/png");
  }

  return (
    <div className="coin-image" data-testid="coin-image">
      <div className="coin-image-preview">
        <canvas
          ref={canvas}
          width={COIN_SIZE}
          height={COIN_SIZE}
          data-testid="coin-canvas"
          aria-label="Token image preview"
        />
        <span className="coin-image-size">{COIN_SIZE}×{COIN_SIZE} PNG</span>
      </div>

      <div className="coin-image-controls">
        {apiConfigured ? (
          <div className="coin-ai" data-testid="coin-ai">
            {quota ? (
              <>
                <label>
                  Describe the coin
                  <input
                    data-testid="coin-prompt"
                    value={prompt}
                    maxLength={2048}
                    placeholder="a minimalist gold owl coin, flat vector, dark background"
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") void generate(); }}
                  />
                </label>
                <div className="row">
                  <button data-testid="coin-generate" onClick={() => void generate()} disabled={busy || !prompt.trim()}>
                    {busy ? "Generating…" : "Generate"}
                  </button>
                  <span className="coin-image-note" data-testid="coin-quota">
                    {quota.remaining} of {quota.limit} left today
                  </span>
                </div>
              </>
            ) : (
              <p className="coin-image-note" data-testid="coin-ai-signin">
                Sign in to use the image generator. It runs on the deployment's own
                account and is capped per person per day, so it is behind a session —
                launching a token is not, and never will be.
              </p>
            )}
            {aiNote ? <p className="coin-image-note" data-testid="coin-ai-note">{aiNote}</p> : null}
          </div>
        ) : null}

        <label>
          Ticker on the coin
          <input
            data-testid="coin-ticker"
            value={style.ticker}
            maxLength={5}
            onChange={(e) => edit("ticker", normalizeTicker(e.target.value))}
            placeholder={normalizeTicker(symbol) || "OXM"}
          />
        </label>

        <div className="row">
          <label>
            Shape
            <select
              data-testid="coin-shape"
              value={style.shape}
              onChange={(e) => edit("shape", e.target.value as CoinShape)}
            >
              <option value="circle">Circle</option>
              <option value="square">Square</option>
            </select>
          </label>
          <label>
            Fill
            <select
              data-testid="coin-fill"
              value={style.fill}
              onChange={(e) => edit("fill", e.target.value as CoinFill)}
            >
              <option value="gradient">Gradient</option>
              <option value="solid">Solid</option>
            </select>
          </label>
        </div>

        <div className="row">
          <label>
            Colour
            <input
              type="color"
              data-testid="coin-color-a"
              value={isHexColor(style.colorA) ? style.colorA : "#7c35ef"}
              onChange={(e) => edit("colorA", e.target.value)}
            />
          </label>
          <label>
            Second colour
            <input
              type="color"
              data-testid="coin-color-b"
              value={isHexColor(style.colorB) ? style.colorB : "#526ff1"}
              disabled={style.fill !== "gradient"}
              onChange={(e) => edit("colorB", e.target.value)}
            />
          </label>
        </div>

        <div className="row">
          <button data-testid="coin-download" onClick={download}>Download PNG</button>
          <label className="btnlike">
            Upload an image
            <input
              type="file"
              accept="image/*"
              data-testid="coin-upload"
              style={{ display: "none" }}
              onChange={(e) => onFile(e.target.files?.[0])}
            />
          </label>
        </div>

        {uploadNote ? <p className="coin-image-note" data-testid="coin-upload-note">{uploadNote}</p> : null}

        {/*
            The one thing this component must be honest about. It makes a file;
            it does not host it, and nothing it produces can be pasted into the
            form as-is.
        */}
        <p className="coin-image-note" data-testid="coin-hosting-note">
          {saved
            ? `Saved ${saved}. Host it somewhere permanent, then paste its URL into Image URL above.`
            : "This makes a file on your machine. Host it somewhere permanent (IPFS, Arweave, or a domain you will keep), then paste its URL into Image URL above — the image URI is written on chain before authorities are revoked, so a host you stop paying for makes the token blank forever."}
        </p>

        {imageUrl ? (
          <div className="coin-image-live" data-testid="coin-live">
            <img src={imageUrl} alt="" width={44} height={44} />
            <span>Image URL above, as a wallet would load it. A broken picture here is a broken token image.</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
